import { ECartTypeId } from '@ringana/configs';
import moment from 'moment';
import { IImage } from '@/common/interfaces/image.interface';
import config from '../../config';
import { EErrorMessage } from '../common/enums/messages.enum';
import { IShoppingCart, IShoppingCartIndividual } from '../common/interfaces/cart.interface';
import { ICustomerOrder } from '../common/interfaces/customers.interface';
import {
  IDocuSignRecipientViewData,
  IFrenchPartnerApplicationDocumentOptions,
  IGenericPartnerApplicationDocumentOptions,
  IItalianPartnerApplicationDocumentOptions,
  IPartnerApplication,
  IPartnerApplicationAddItemToCartRequestBody,
} from '../common/interfaces/partner-application.interface';
import { IPartnerSearchResult } from '../common/interfaces/partners.interface';
import { IProduct } from '../common/interfaces/products.interface';
import ApplicationError from '../errors/application-error';
import CookieHelper from '../helpers/CookieHelper';
import ErrorHandlerHelper from '../helpers/ErrorHandlerHelper';
import OrderHelper from '../helpers/OrderHelper';
import PartnerApplicationHelper from '../helpers/page-specific/PartnerApplicationHelper';
import ZApiService from './ZApiService';
import ABApiService from './ABApiService';

export default class PartnerApplicationService {
  private zApiService: ZApiService;
  private abApiService: ABApiService;
  private partnerApplicationProducts: IProduct[] | null;

  constructor(zApiService: ZApiService, abApiService: ABApiService) {
    this.zApiService = zApiService;
    this.abApiService = abApiService;
    this.partnerApplicationProducts = null;
  }

  private static checkForAuthToken(): void {
    if(!CookieHelper.getAuthToken()) {
      throw new ApplicationError(EErrorMessage.UNAUTHORIZED);
    }
  }

  public async addItemToCart(
    matchcode: string,
    amount: number,
    cartId: number,
    unique: boolean,
  ): Promise<IShoppingCartIndividual> {
    PartnerApplicationService.checkForAuthToken();
    const requestOptions = {
      data: {
        amount,
        cartId,
        isPartnerApplication: true,
        matchcode,
      } as IPartnerApplicationAddItemToCartRequestBody,
    };
    if(unique) {
      requestOptions.data.matchcodeBlocked = requestOptions.data.matchcode;
    }
    const responseData = await this.zApiService.addItemToCart({
      amount,
      isPartnerApplication: true,
      matchcode,
      matchcodeBlocked: unique ? matchcode : undefined,
    }, cartId);
    return PartnerApplicationHelper.findPartnerApplicationCart(responseData);
  }

  public async getPartnerApplication(addressId: number): Promise<IPartnerApplication> {
    return this.zApiService.getPartnerApplication(addressId);
  }

  public async removeItemFromCart(cartId: number, cartContentId: number): Promise<IShoppingCartIndividual> {
    PartnerApplicationService.checkForAuthToken();
    const responseData = await this.zApiService.removeItemFromCart(cartContentId, cartId, true);
    return this.fetchPartnerApplicationCart(responseData);
  }

  public async fetchPartnerApplicationCart(data: IShoppingCart): Promise<IShoppingCartIndividual> {
    if(!data) {
      throw new ApplicationError(EErrorMessage.VALIDATION_PARAMETER_FAILED);
    }
    const shoppingCart = data.shoppingCarts.find(cart => cart.cartTypeId === config.cartTypes.PARTNERAPPLICATION);
    if(!shoppingCart) {
      throw new ApplicationError(EErrorMessage.NOT_FOUND);
    }
    return shoppingCart;
  }

  public async getPartnerData(partnerId: string | number): Promise<IPartnerSearchResult> {
    try {
      const response = await this.zApiService.postPartnerSearch(partnerId.toString());
      return response[0];
    } catch (err) {
      throw new ApplicationError((err as Error).message);
    }
  }

  public async hasOpenPartnerApplicationOrders(partnerApplicationProducts: Map<string, IProduct>): Promise<number | false> {
    const filterOrders = {
      dateFrom: moment(moment().subtract(90, `days`)).format(`DD.MM.YYYY`),
      dateTo: moment().format(`DD.MM.YYYY`),
      status: `offen`,
    };
    const orders: ICustomerOrder[] = (await OrderHelper.getOrders(filterOrders)).data;

    for(const order of orders) {
      if(order.paymentIsRepeatable) {
        let hasPartnerApplicationProduct: string | false = false;
        order.items.forEach(item => {
          Array.from(partnerApplicationProducts.values()).forEach(check => {
            if(item.matchcode === check.matchcode) {
              hasPartnerApplicationProduct = order.orderNumber;
            }
          });
        });
        if(hasPartnerApplicationProduct) {
          return hasPartnerApplicationProduct;
        }
      }
    }
    return false;
  }

  public async getPartnerApplicationProducts(errorMessage: string): Promise<IProduct[]> {
    if(!this.partnerApplicationProducts) {
      try {
        this.partnerApplicationProducts = await this.zApiService.getPartnerApplicationProducts();
      } catch (err) {
        ErrorHandlerHelper.handleError((err as Error), {
          logToConsole: true,
          showSwal: true,
          swalMessage: errorMessage,
        });
        this.partnerApplicationProducts = [];
      }
    }
    return this.partnerApplicationProducts as IProduct[];
  }

  public async fetchCart(id: number, errorMessage: string): Promise<IShoppingCartIndividual> {
    PartnerApplicationService.checkForAuthToken();
    try {
      const cart = await this.zApiService.getCarts(id);
      let shoppingCart: IShoppingCartIndividual | undefined;
      if(!cart) {
        shoppingCart = await this.createShoppingCart(errorMessage);
      } else {
        shoppingCart = cart.shoppingCarts.find(individualCart => individualCart.cartTypeId === config.cartTypes.PARTNERAPPLICATION);
        if(!shoppingCart) {
          shoppingCart = await this.createShoppingCart(errorMessage);
        }
      }
      return shoppingCart;
    } catch (err) {
      throw new ApplicationError(errorMessage);
    }
  }

  public async getDocuSignRecipientViewData(
    options: IItalianPartnerApplicationDocumentOptions | IFrenchPartnerApplicationDocumentOptions | IGenericPartnerApplicationDocumentOptions,
  ): Promise<IDocuSignRecipientViewData> {
    return this.zApiService.getDocuSignRecipientViewData(options);
  }

  private async createShoppingCart(errorMessage: string): Promise<IShoppingCartIndividual> {
    const cartResponse = await this.zApiService.postCartCreate(ECartTypeId.PARTNER_APPLICATION, true);
    const shoppingCart = cartResponse.shoppingCarts.find(individualCart => individualCart.cartTypeId === config.cartTypes.PARTNERAPPLICATION);
    if(!shoppingCart) {
      throw new ApplicationError(errorMessage);
    }
    return shoppingCart;
  }

  public async postItalianPartnerPicture(image: IImage): Promise<IImage> {
    return this.zApiService.postItalianPartnerPicture(image);
  }

  public async updateItalianPartnerPicture(image: IImage): Promise<IImage> {
    return this.zApiService.updateItalianPartnerPicture(image);
  }

  public async getItalianPartnerPicture(): Promise<IImage> {
    return this.zApiService.getItalianPartnerPicture();
  }

  public async getUnderMaintenance(): Promise<boolean> {
    try {
      const settings = await this.abApiService.getGeneralSettings();
      return settings.partnerApplicationUnderMaintenance ?? false;
    } catch (err) {
      ErrorHandlerHelper.handleError(err, { logToConsole: true });
      return false;
    }
  }
}
